import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Map, RotateCcw, Binary, FileText, Lock, Unlock, ChevronLeft, ChevronRight, Braces, AlertTriangle, Hash } from 'lucide-react';
import { ITEMS } from '@/config/constants';
import LeonDialogue from '@/components/LeonDialogue';
import CONFIG from '@/config/siteConfig';
import { PUZZLE_4_CIPHER_DECODE } from '@/config/missionContent';

const CORRECT_SHIFT = PUZZLE_4_CIPHER_DECODE.shift;
const CORRECT_RESULT = PUZZLE_4_CIPHER_DECODE.targetText;

// Generate the encrypted text by applying the reverse shift
const CIPHER_TEXT = CORRECT_RESULT.split('').map(ch => {
  if (ch >= 'A' && ch <= 'Z') {
    return String.fromCharCode(((ch.charCodeAt(0) - 65 - CORRECT_SHIFT + 26) % 26) + 65);
  }
  return ch;
}).join('');

const decrypt = (text, shift) => {
  return text.split('').map(ch => {
    if (ch >= 'A' && ch <= 'Z') {
      return String.fromCharCode(((ch.charCodeAt(0) - 65 + shift) % 26) + 65);
    }
    return ch;
  }).join('');
};

/* ── Background & Atmosphere ── */
function CipherBackground() {
  const particles = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      left: `${6 + (i * 8.3) % 88}%`,
      top: `${4 + (i * 12.1) % 92}%`,
      size: 1 + (i % 3) * 0.5,
      duration: 7 + (i % 4) * 2.5,
      delay: i * 0.7,
      color: i % 3 === 0 ? 'bg-purple-500/15' : i % 3 === 1 ? 'bg-cyan-500/10' : 'bg-violet-400/10',
    })), []);

  const codeColumns = useMemo(() =>
    Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: `${i * 10}%`,
      chars: Array.from({ length: 20 }, (_, j) => String.fromCharCode(65 + ((i * 7 + j * 3) % 26))),
      duration: 5 + (i % 4) * 2,
      delay: (i * 1.3) % 4,
    })), []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-[#050210] to-black" />

      {/* Matrix code columns */}
      {codeColumns.map(col => (
        <motion.div
          key={col.id}
          initial={{ y: '-100%' }}
          animate={{ y: '100%' }}
          transition={{ duration: col.duration, delay: col.delay, repeat: Infinity, ease: 'linear' }}
          className="absolute font-terminal text-xs text-purple-500/[0.06] leading-tight whitespace-nowrap"
          style={{ left: col.left }}
        >
          {col.chars.map((ch, j) => <div key={j}>{ch}</div>)}
        </motion.div>
      ))}

      {/* Floating particles */}
      {particles.map(p => (
        <motion.div
          key={p.id}
          animate={{ y: [0, -15, 0], x: [0, 5, -3, 0], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
          className={`absolute rounded-full ${p.color}`}
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
        />
      ))}

      {/* Pulsing purple vignette */}
      <div
        className="absolute inset-0 animate-pulse"
        style={{
          animationDuration: '5s',
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(30,5,50,0.2) 100%)',
        }}
      />

      {/* CRT scanlines */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(168,85,247,0.1) 2px, rgba(168,85,247,0.1) 4px)',
          backgroundSize: '100% 4px',
        }}
      />
    </div>
  );
}

/* ── HUD Corners ── */
function HUDCorners({ color = 'border-purple-700/30' }) {
  return (
    <>
      <div className={`absolute top-0 left-0 w-6 h-6 border-t border-l ${color}`} />
      <div className={`absolute top-0 right-0 w-6 h-6 border-t border-r ${color}`} />
      <div className={`absolute bottom-0 left-0 w-6 h-6 border-b border-l ${color}`} />
      <div className={`absolute bottom-0 right-0 w-6 h-6 border-b border-r ${color}`} />
    </>
  );
}

/* ── Tutorial Screen ── */
function CipherTutorial({ onStart }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-md mx-auto relative z-10"
    >
      <div className="bg-black/60 backdrop-blur-sm border border-purple-900/30 rounded-lg p-6 relative">
        <HUDCorners />

        {/* Header */}
        <div className="text-center mb-5">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Binary size={14} className="text-purple-400" />
            <span className="font-terminal text-xs tracking-[4px] text-purple-400 uppercase">Decryption Protocol</span>
            <Binary size={14} className="text-purple-400" />
          </div>
          <div className="w-16 h-px bg-gradient-to-r from-transparent via-purple-700/50 to-transparent mx-auto" />
        </div>

        {/* How it works */}
        <div className="mb-5">
          <h3 className="font-terminal text-xs tracking-[3px] text-purple-400 uppercase mb-3">How It Works</h3>
          <div className="space-y-2.5">
            {[
              { icon: Lock, text: 'The message is encrypted with a Caesar cipher — each letter shifted by a fixed amount', color: 'text-purple-400' },
              { icon: RotateCcw, text: 'Use the wheel, buttons, or slider to adjust the shift value (0-25)', color: 'text-cyan-400' },
              { icon: Unlock, text: 'When the decoded text makes sense, confirm to complete decryption', color: 'text-green-400' },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.15 }}
                className="flex items-center gap-3"
              >
                <div className="w-7 h-7 rounded border border-purple-800/30 bg-purple-950/30 flex items-center justify-center shrink-0">
                  <item.icon size={14} className={item.color} />
                </div>
                <span className="text-xs text-gray-400 font-rajdhani">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Visual example */}
        <div className="mb-5 bg-black/40 border border-cyan-900/20 rounded p-3">
          <div className="flex items-center gap-2 mb-2">
            <Binary size={10} className="text-cyan-600" />
            <span className="text-xs font-terminal tracking-[3px] text-cyan-600 uppercase">Example</span>
          </div>
          <div className="flex items-center justify-center gap-4 py-2">
            <div className="text-center">
              <p className="text-xs font-terminal text-gray-600 mb-1">ORIGINAL</p>
              <span className="font-orbitron text-xl text-red-400">A</span>
            </div>
            <div className="text-center">
              <p className="text-xs font-terminal text-purple-500 mb-1">SHIFT +3</p>
              <ChevronRight size={16} className="text-purple-500 mx-auto" />
            </div>
            <div className="text-center">
              <p className="text-xs font-terminal text-gray-600 mb-1">RESULT</p>
              <span className="font-orbitron text-xl text-green-400">D</span>
            </div>
          </div>
        </div>

        {/* Warning */}
        <div className="flex items-center gap-2 bg-purple-950/20 border border-purple-800/20 rounded px-3 py-2 mb-5">
          <AlertTriangle size={12} className="text-yellow-600 shrink-0" />
          <span className="text-xs text-yellow-600/80 font-terminal tracking-wider">26 POSSIBLE SHIFT VALUES -- ONLY ONE REVEALS THE MESSAGE</span>
        </div>

        {/* Start button */}
        <motion.button
          onClick={onStart}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full py-3 bg-gradient-to-r from-purple-950/50 via-purple-900/30 to-purple-950/50 border border-purple-600/40 rounded font-terminal text-sm tracking-[4px] text-purple-300 uppercase relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
          <span className="relative flex items-center justify-center gap-2">
            <Binary size={14} />
            Begin Decryption
            <ChevronRight size={14} />
          </span>
          <motion.div
            animate={{ opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 border border-purple-500/20 rounded"
          />
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ── CipherHUD ── */
function CipherHUD({ shift, adjustments }) {
  return (
    <div className="max-w-lg mx-auto mb-4 relative z-10">
      <div className="bg-black/50 border border-purple-900/30 rounded px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
            <span className="text-xs font-terminal text-purple-700/60 tracking-[3px]">CHECKPOINT 07 // INTEL DECRYPTION</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Hash size={10} className="text-cyan-600" />
              <span className="text-xs font-terminal text-gray-600">SHIFT:</span>
              <span className="text-sm font-orbitron text-cyan-400">{shift}</span>
            </div>
            <div className="h-4 w-px bg-purple-900/30" />
            <div className="flex items-center gap-1.5">
              <RotateCcw size={10} className="text-purple-600" />
              <span className="text-xs font-terminal text-gray-600">ADJ:</span>
              <span className="text-sm font-orbitron text-purple-400">{adjustments}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Enhanced CipherWheel ── */
function CipherWheel({ shift, isCorrect }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;

    // Clear
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, size, size);

    // Outer ring (original)
    const outerRadius = center - 10;
    ctx.beginPath();
    ctx.arc(center, center, outerRadius, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(0,255,65,0.15)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Inner ring (shifted)
    const innerRadius = center - 35;
    ctx.beginPath();
    ctx.arc(center, center, innerRadius, 0, Math.PI * 2);
    ctx.strokeStyle = isCorrect ? 'rgba(34,197,94,0.4)' : 'rgba(168,85,247,0.3)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw outer letters (original A-Z)
    ctx.font = '10px monospace';
    ctx.fillStyle = 'rgba(0,255,65,0.5)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < 26; i++) {
      const angle = (i / 26) * Math.PI * 2 - Math.PI / 2;
      const x = center + Math.cos(angle) * (outerRadius - 15);
      const y = center + Math.sin(angle) * (outerRadius - 15);
      ctx.fillText(String.fromCharCode(65 + i), x, y);
    }

    // Draw inner letters (shifted)
    ctx.fillStyle = isCorrect ? 'rgba(34,197,94,0.8)' : 'rgba(168,85,247,0.7)';
    for (let i = 0; i < 26; i++) {
      const angle = (i / 26) * Math.PI * 2 - Math.PI / 2;
      const shiftedIdx = (i + shift) % 26;
      const x = center + Math.cos(angle) * (innerRadius - 15);
      const y = center + Math.sin(angle) * (innerRadius - 15);
      ctx.fillText(String.fromCharCode(65 + shiftedIdx), x, y);
    }

    // Tick mark at current shift position
    const tickAngle = (shift / 26) * Math.PI * 2 - Math.PI / 2;
    ctx.beginPath();
    ctx.moveTo(center + Math.cos(tickAngle) * (outerRadius - 2), center + Math.sin(tickAngle) * (outerRadius - 2));
    ctx.lineTo(center + Math.cos(tickAngle) * (outerRadius + 4), center + Math.sin(tickAngle) * (outerRadius + 4));
    ctx.strokeStyle = isCorrect ? '#22c55e' : '#06b6d4';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Center
    ctx.beginPath();
    ctx.arc(center, center, 20, 0, Math.PI * 2);
    ctx.fillStyle = isCorrect ? 'rgba(34,197,94,0.2)' : 'rgba(168,85,247,0.1)';
    ctx.fill();
    ctx.strokeStyle = isCorrect ? 'rgba(34,197,94,0.5)' : 'rgba(168,85,247,0.3)';
    ctx.stroke();

    // Shift number
    ctx.fillStyle = isCorrect ? '#22c55e' : '#a855f7';
    ctx.font = 'bold 14px monospace';
    ctx.fillText(shift.toString(), center, center);

  }, [shift, isCorrect]);

  return (
    <canvas
      ref={canvasRef}
      width={180}
      height={180}
      className="mx-auto rounded-full border border-purple-900/30"
      style={{
        boxShadow: isCorrect
          ? '0 0 20px rgba(34,197,94,0.2), 0 0 40px rgba(34,197,94,0.1)'
          : '0 0 15px rgba(168,85,247,0.15), 0 0 30px rgba(6,182,212,0.05)',
      }}
    />
  );
}

/* ── Main Component ── */
export default function Puzzle4CipherDecode({ onComplete, onItemCollected }) {
  const [showLeon, setShowLeon] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [puzzleActive, setPuzzleActive] = useState(false);
  const [shift, setShift] = useState(0);
  const [solved, setSolved] = useState(false);
  const [showFinalLeon, setShowFinalLeon] = useState(false);
  const [successPhase, setSuccessPhase] = useState(0);
  const [showProceed, setShowProceed] = useState(false);
  const [adjustments, setAdjustments] = useState(0);

  const decrypted = decrypt(CIPHER_TEXT, shift);
  const isCorrect = shift === CORRECT_SHIFT;

  // Mount-reset
  useEffect(() => {
    setShowLeon(true);
    setShowTutorial(false);
    setPuzzleActive(false);
    setShift(0);
    setSolved(false);
    setShowFinalLeon(false);
    setSuccessPhase(0);
    setShowProceed(false);
    setAdjustments(0);
  }, []);

  const handleLeonDismiss = useCallback(() => {
    setShowLeon(false);
    setShowTutorial(true);
  }, []);

  const handleTutorialStart = useCallback(() => {
    setShowTutorial(false);
    setPuzzleActive(true);
  }, []);

  const handleConfirm = useCallback(() => {
    if (isCorrect) {
      setSolved(true);
      setPuzzleActive(false);
      // Multi-phase success
      setSuccessPhase(1);
      setTimeout(() => setSuccessPhase(2), 500);
      setTimeout(() => setSuccessPhase(3), 1200);
      setTimeout(() => setSuccessPhase(4), 1800);
      setTimeout(() => setShowFinalLeon(true), 2200);
    }
  }, [isCorrect]);

  const handleFinalDismiss = useCallback(() => {
    setShowFinalLeon(false);
    onItemCollected?.(ITEMS.ENCRYPTED_MAP);
    setShowProceed(true);
  }, [onItemCollected]);

  const adjustShift = useCallback((delta) => {
    setShift(prev => (prev + delta + 26) % 26);
    setAdjustments(prev => prev + 1);
  }, []);

  const handleSliderChange = useCallback((e) => {
    const newVal = Number(e.target.value);
    setShift(prev => {
      if (newVal !== prev) {
        setAdjustments(a => a + 1);
      }
      return newVal;
    });
  }, []);

  return (
    <div className="puzzle-container relative overflow-hidden" data-testid="puzzle-cipher-decode">
      <CipherBackground />

      {/* Success Phase 1: Green flash + shockwave */}
      <AnimatePresence>
        {successPhase >= 1 && (
          <motion.div
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 1 }}
            className="absolute inset-0 bg-green-500/10 pointer-events-none z-40"
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="text-center mb-5 relative z-10">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-8 h-px bg-gradient-to-r from-transparent to-purple-700/40" />
          <Binary size={12} className="text-purple-500" />
          <p className="text-xs font-terminal tracking-[6px] text-purple-500 uppercase">Checkpoint 07</p>
          <Binary size={12} className="text-purple-500" />
          <div className="w-8 h-px bg-gradient-to-l from-transparent to-purple-700/40" />
        </div>
        <h2 className="font-orbitron text-2xl tracking-wider text-gray-200 uppercase">Intel Decryption</h2>
        <p className="text-sm text-purple-700/60 font-terminal mt-1">Caesar Cipher // Shift the alphabet to decode</p>
      </div>

      {/* Tutorial */}
      <AnimatePresence>
        {showTutorial && <CipherTutorial onStart={handleTutorialStart} />}
      </AnimatePresence>

      {/* HUD */}
      {(puzzleActive || solved) && (
        <CipherHUD shift={shift} adjustments={adjustments} />
      )}

      {/* Puzzle body */}
      {(puzzleActive || solved) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-lg mx-auto relative z-10">

          {/* Encrypted message panel */}
          <div className="bg-black/50 border border-red-900/20 rounded-lg p-5 relative overflow-hidden">
            <HUDCorners color="border-red-800/30" />

            <div className="flex items-center gap-2 mb-3">
              <Lock size={12} className="text-red-500" />
              <span className="text-xs font-terminal tracking-[3px] text-red-500 uppercase">Encrypted Transmission</span>
            </div>

            <div className="bg-red-950/10 border border-red-900/30 rounded p-4 text-center">
              <motion.p
                animate={{ opacity: [0.8, 1, 0.8] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="font-orbitron text-lg md:text-xl tracking-wider text-red-400"
                style={{ textShadow: '0 0 8px rgba(255,0,0,0.3)' }}
              >
                {CIPHER_TEXT}
              </motion.p>
            </div>
          </div>

          {/* Cipher wheel and controls */}
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <CipherWheel shift={shift} isCorrect={isCorrect} />
            </div>

            {/* Controls */}
            <div className="flex-1 space-y-4 w-full">
              {/* Shift controls */}
              <div className="flex items-center justify-center gap-3">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => adjustShift(-1)}
                  disabled={solved}
                  className="w-10 h-10 rounded-lg border border-purple-700/40 bg-purple-950/20 flex items-center justify-center text-purple-400 hover:border-purple-500/60 transition-colors"
                >
                  <ChevronLeft size={18} />
                </motion.button>

                <div className="text-center px-4">
                  <p className="text-xs font-terminal text-gray-600 tracking-wider mb-1">SHIFT VALUE</p>
                  <span className="font-orbitron text-3xl text-purple-400">{shift}</span>
                </div>

                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => adjustShift(1)}
                  disabled={solved}
                  className="w-10 h-10 rounded-lg border border-purple-700/40 bg-purple-950/20 flex items-center justify-center text-purple-400 hover:border-purple-500/60 transition-colors"
                >
                  <ChevronRight size={18} />
                </motion.button>
              </div>

              {/* Slider */}
              <div className="space-y-1">
                <input
                  type="range"
                  min="0"
                  max="25"
                  value={shift}
                  onChange={handleSliderChange}
                  disabled={solved}
                  className="w-full h-2 bg-gray-900 rounded-lg appearance-none cursor-pointer accent-purple-500"
                  data-testid="cipher-slider"
                />
                <div className="flex justify-between text-xs text-gray-600 font-terminal">
                  <span>0</span>
                  <span>13</span>
                  <span>25</span>
                </div>
              </div>
            </div>
          </div>

          {/* Decrypted preview */}
          <div className={`bg-black/50 border rounded-lg p-5 transition-all duration-500 relative overflow-hidden
            ${isCorrect ? 'border-green-500/40' : 'border-gray-800/30'}`}
          >
            {isCorrect && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 bg-green-500/5 pointer-events-none"
              />
            )}
            <HUDCorners color={isCorrect ? 'border-green-600/30' : 'border-gray-700/20'} />

            <div className="flex items-center gap-2 mb-3">
              {isCorrect ? (
                <Unlock size={12} className="text-green-400" />
              ) : (
                <Braces size={12} className="text-gray-500" />
              )}
              <span className={`text-xs font-terminal tracking-[3px] uppercase ${isCorrect ? 'text-green-400' : 'text-gray-500'}`}>
                {isCorrect ? 'Decryption Complete' : 'Decoded Output'}
              </span>
            </div>

            <div className={`rounded p-4 text-center ${isCorrect ? 'bg-green-950/20 border border-green-500/30' : 'bg-black/30 border border-gray-800/30'}`}>
              <p
                className={`font-orbitron text-lg md:text-xl tracking-wider transition-colors ${isCorrect ? 'text-green-400' : 'text-gray-400'}`}
                data-testid="decoded-text"
                style={isCorrect ? { textShadow: '0 0 10px rgba(34,197,94,0.4)' } : {}}
              >
                {decrypted}
              </p>
            </div>
          </div>

          {/* Confirm button */}
          {!solved && (
            <div className="text-center">
              <motion.button
                whileHover={isCorrect ? { scale: 1.02, boxShadow: '0 0 25px rgba(34,197,94,0.3)' } : {}}
                whileTap={isCorrect ? { scale: 0.98 } : {}}
                onClick={handleConfirm}
                disabled={!isCorrect}
                className={`group relative px-10 py-3 border-2 font-orbitron text-sm tracking-[4px] uppercase transition-all overflow-hidden rounded
                  ${isCorrect
                    ? 'bg-green-900/20 border-green-500 text-green-400 hover:bg-green-900/30'
                    : 'bg-gray-800/20 border-gray-700 text-gray-600 cursor-not-allowed'
                  }`}
                data-testid="confirm-decode-btn"
              >
                {isCorrect && (
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/10 to-transparent pointer-events-none"
                    animate={{ x: ['-200%', '200%'] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                  />
                )}
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <FileText size={14} />
                  Confirm Decode
                </span>
              </motion.button>

              {!isCorrect && (
                <p className="text-xs text-gray-600 font-special italic mt-3">
                  Adjust the shift until the message becomes clear...
                </p>
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* Multi-phase success */}
      {solved && (
        <div className="text-center space-y-4 mt-6 relative z-10">
          {/* Phase 2: Map icon with rotating dashed circle */}
          {successPhase >= 2 && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 15 }}
              className="relative w-20 h-20 mx-auto"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-0 border-2 border-dashed border-green-500/20 rounded-full"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-16 h-16 rounded-full bg-green-900/30 border-2 border-green-500 flex items-center justify-center"
                  style={{ boxShadow: '0 0 30px rgba(34,197,94,0.25)' }}
                >
                  <Map size={28} className="text-green-400" />
                </div>
              </div>
              {/* Shockwave ring */}
              <motion.div
                initial={{ scale: 0.5, opacity: 0.8 }}
                animate={{ scale: 3, opacity: 0 }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
                className="absolute inset-0 border border-green-500/40 rounded-full"
              />
            </motion.div>
          )}

          {/* Phase 3: Title */}
          {successPhase >= 3 && (
            <motion.p
              initial={{ letterSpacing: '2px', opacity: 0 }}
              animate={{ letterSpacing: '6px', opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="font-orbitron text-green-400 text-sm"
            >
              MESSAGE DECODED
            </motion.p>
          )}

          {/* Phase 4: Stats */}
          {successPhase >= 4 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-3"
            >
              <div className="inline-flex items-center gap-4 bg-purple-950/20 border border-purple-800/20 rounded px-4 py-2">
                <div className="text-center">
                  <p className="text-xs font-terminal text-gray-600 tracking-wider">SHIFT FOUND</p>
                  <p className="text-lg font-orbitron text-cyan-400">{shift}</p>
                </div>
                <div className="w-px h-8 bg-purple-800/30" />
                <div className="text-center">
                  <p className="text-xs font-terminal text-gray-600 tracking-wider">ADJUSTMENTS</p>
                  <p className="text-lg font-orbitron text-purple-400">{adjustments}</p>
                </div>
              </div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-gray-400 font-rajdhani"
                style={{ textShadow: '0 0 10px rgba(234,179,8,0.3)' }}
              >
                Encrypted Map acquired
              </motion.p>
            </motion.div>
          )}
        </div>
      )}

      {showProceed && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="text-center mt-8 relative z-20"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onComplete?.()}
            className="inline-flex items-center gap-2 px-8 py-3 rounded font-orbitron text-sm tracking-widest text-black relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #22c55e, #00ff41)' }}
          >
            <span className="relative z-10">PROCEED</span>
            <motion.div
              className="absolute inset-0"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)', width: '50%' }}
            />
          </motion.button>
        </motion.div>
      )}

      {/* Leon dialogues */}
      {showLeon && (
        <LeonDialogue
          message={PUZZLE_4_CIPHER_DECODE.leon.intro}
          onDismiss={handleLeonDismiss}
        />
      )}

      {showFinalLeon && (
        <LeonDialogue
          message={PUZZLE_4_CIPHER_DECODE.leon.complete}
          onDismiss={handleFinalDismiss}
        />
      )}
    </div>
  );
}
